import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderSlot as _renderSlot, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createVNode as _createVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = { class: "text" }
const _hoisted_3 = { class: "input-block-container" }
const _hoisted_4 = { class: "input-block" }
const _hoisted_5 = {
  key: 0,
  class: "icon-left"
}
const _hoisted_6 = {
  key: 1,
  class: "icon-right"
}
const _hoisted_7 = ["for"]
const _hoisted_8 = {
  key: 3,
  class: "unused-characters"
}
const _hoisted_9 = {
  key: 1,
  class: "error-block"
}
const _hoisted_10 = { class: "message" }

import ValidationService from "@/services/validation.service";
import { VIconSvg } from "@/components";
import VErrorTooltip from "../VErrorTooltip/VErrorTooltip.vue";
import { ref, computed, reactive, useSlots } from "vue";
import { getRandomId } from "@/mixins/global.mixin";
import { TranslateResult } from "vue-i18n";
import i18n from "@/plugins/vue-i18n";
import { isExistSlot } from "@/plugins/helpers";

interface Props {
  label?: string | TranslateResult;
  maxLength?: string | number;
  placeholder?: string | TranslateResult;
  value?: string | number;
  disabled?: boolean;
  size?: string;
  type?: string;
  validationRule?: string;
  variant?: string;
  error?: string | TranslateResult;
  required?: boolean;
  description?: string | TranslateResult;
  showSymbolsCounter?: boolean;
  errorMode?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VInput',
  props: {
    label: { default: "" },
    maxLength: { default: "255" },
    placeholder: { default: "" },
    value: { default: "" },
    disabled: { type: Boolean, default: false },
    size: { default: "md" },
    type: { default: "text" },
    validationRule: { default: "" },
    variant: { default: "regular" },
    error: { default: "" },
    required: { type: Boolean, default: false },
    description: { default: "" },
    showSymbolsCounter: { type: Boolean, default: false },
    errorMode: { default: "tooltip" }
  },
  emits: ["input"],
  setup(__props: any, { emit: __emit }) {

const {
  INTEGER_REG_EXP,
  FLOAT_REG_EXP,
  HUNDREDTHS_REG_EXP,
  ALPHA_NUM_UNDERSCORE_REG_EXP,
  PERCENTAGES_REG_EXP,
} = ValidationService;

const VALIDATION_RULES = reactive({
  float: FLOAT_REG_EXP,
  hundredths: HUNDREDTHS_REG_EXP,
  integer: INTEGER_REG_EXP,
  "alpha-num-underscore": ALPHA_NUM_UNDERSCORE_REG_EXP,
  percentages: PERCENTAGES_REG_EXP,
});

const emit = __emit;

const props = __props;

const input = ref();
const isTextType = ref(false);
const slots = useSlots();

const id = computed(() => {
  return `input-${getRandomId()}`;
});

const placeholderText = computed(() => {
  return props.placeholder ? props.placeholder : i18n.t("placeholder.typeHere");
});

const inputWrapperClasses = computed(() => {
  const variant = `input-${props.variant}`;
  const size = `input-${props.variant}-size-${props.size}`;
  const classes = {
    "input-error": props.error,
    "input-required": props.required,
    "input-disabled": props.disabled,
  };

  return [variant, size, classes];
});

const isShownInfoIcon = computed(() => {
  return props.variant === "regular" && !!props.description;
});

const isShownLabel = computed(() => {
  let show = !!props.label.length;

  if (props.variant === "inner") {
    if (!props.value.toString().length) {
      show = false;
    }
  }

  return show;
});

const isPasswordType = computed(() => {
  return props.type === "password";
});

const isShownMessageError = computed(() => {
  return props.error && props.errorMode === "message";
});

const isShownTooltipError = computed(() => {
  return props.error && props.errorMode === "tooltip";
});

const inputType = computed(() => {
  return isTextType.value ? "text" : props.type;
});

const passwordIconName = computed(() => {
  return isTextType.value ? "Show" : "Hide";
});

const unusedCharactersLength = computed(() => {
  return +props.maxLength - (props.value || "").toString().length;
});

const onInput = (value: string | number) => {
  if (props.validationRule) {
    const input = document.querySelector(`#${id.value}`);

    if (VALIDATION_RULES[props.validationRule]) {
      value = transformValue(value, VALIDATION_RULES[props.validationRule]);
    } else {
      value = transformValue(value, props.validationRule);
    }

    input.value = value;
  }

  emit("input", value);
};

const transformValue = (value: string | number, exp: string) => {
  const regExp = new RegExp(exp, "ig");

  const matches = String(value).match(regExp);

  return matches ? matches.join("") : "";
};

const onClickShowPassword = () => {
  isTextType.value = !isTextType.value;
};

const onClickSlotIcon = () => {
  input.value.focus();
};

return (_ctx: any,_cache: any) => {
  const _component_t_input = _resolveComponent("t-input")!
  const _directive_touch = _resolveDirective("touch")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["input-wrapper", inputWrapperClasses.value])
  }, [
    (isShownLabel.value)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          class: "label",
          for: id.value
        }, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.label), 1),
          (isShownInfoIcon.value)
            ? (_openBlock(), _createBlock(_unref(VIconSvg), {
                key: 0,
                tooltip: _ctx.description,
                name: "InfoCircle2",
                color: "fc-stroke",
                size: "xs"
              }, null, 8, ["tooltip"]))
            : _createCommentVNode("", true)
        ], 8, _hoisted_1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        (_unref(isExistSlot)('icon-left', _unref(slots)))
          ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_5, [
              _renderSlot(_ctx.$slots, "icon-left")
            ])), [
              [_directive_touch, onClickSlotIcon]
            ])
          : _createCommentVNode("", true),
        (_unref(isExistSlot)('icon-right', _unref(slots)))
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _renderSlot(_ctx.$slots, "icon-right")
            ]))
          : _createCommentVNode("", true),
        (isPasswordType.value)
          ? (_openBlock(), _createElementBlock("label", {
              key: 2,
              class: "password-icon",
              for: id.value
            }, [
              _createVNode(_unref(VIconSvg), {
                name: passwordIconName.value,
                color: "fc-stroke",
                "color-tag": "stroke",
                onClick: onClickShowPassword
              }, null, 8, ["name"])
            ], 8, _hoisted_7))
          : _createCommentVNode("", true),
        _createVNode(_component_t_input, {
          id: id.value,
          ref_key: "input",
          ref: input,
          class: "input",
          classes: {},
          value: _ctx.value,
          placeholder: placeholderText.value,
          type: inputType.value,
          disabled: _ctx.disabled,
          maxlength: _ctx.maxLength,
          autocomplete: "off",
          onInput: onInput
        }, null, 8, ["id", "value", "placeholder", "type", "disabled", "maxlength"]),
        (_ctx.showSymbolsCounter)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(unusedCharactersLength.value), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    (isShownMessageError.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createVNode(_unref(VIconSvg), {
            name: "Danger",
            color: "fc-error",
            size: "xs"
          }),
          _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.error), 1)
        ]))
      : _createCommentVNode("", true),
    (isShownTooltipError.value)
      ? (_openBlock(), _createBlock(VErrorTooltip, {
          key: 2,
          error: _ctx.error
        }, null, 8, ["error"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})